import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserForm, type UserFormValues } from '@/components/users/UserForm';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { getUserRequest, updateUserRequest } from '@/services/users';
import type { UpdateUserBody, UserDetail } from '@/types/users';

export function UserEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const detailQuery = useQuery<UserDetail, ApiError>({
    queryKey: ['user', id],
    queryFn: () => getUserRequest(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  const updateMutation = useMutation<UserDetail, ApiError, { id: number; body: UpdateUserBody }>({
    mutationFn: ({ id: userId, body }) => updateUserRequest(userId, body),
    onSuccess: (updated) => {
      toast.success(`User '${updated.fullName}' saved`);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['user', id] });
      void navigate(`/users/${String(id)}`);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not update user', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('users.update')) {
    return <Navigate to="/users" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/users" replace />;
  }

  function handleSubmit(values: UserFormValues): void {
    setErrorMessage(null);
    const body: UpdateUserBody = {
      fullName: values.fullName,
      email: values.email,
      employeeId: values.employeeId ?? null,
      phoneNumber: values.phoneNumber ?? null,
      roleId: values.roleId,
      // See UserCreatePage — `siteIds` wins server-side; `siteId` is the
      // old-backend fallback and carries the primary.
      siteId: values.siteIds[0] ?? null,
      siteIds: values.siteIds,
      workOrderIds: values.workOrderIds,
    };
    updateMutation.mutate({ id, body });
  }

  const initialValues: Partial<UserFormValues> | undefined =
    detailQuery.data !== undefined
      ? {
          fullName: detailQuery.data.fullName,
          email: detailQuery.data.email,
          employeeId: detailQuery.data.employeeId ?? '',
          phoneNumber: detailQuery.data.phoneNumber ?? '',
          roleId: detailQuery.data.roleId ?? 0,
          siteIds: detailQuery.data.sites.map((s) => s.siteId),
          workOrderIds: detailQuery.data.workOrders.map((w) => w.workOrderId),
        }
      : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit User</h1>
        <p className="text-muted-foreground">Update profile fields and role assignment.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{detailQuery.data?.fullName ?? 'User'}</CardTitle>
          <CardDescription>
            Email and Employee ID must remain unique across the system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="user-edit-loading">
              Loading user…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="user-edit-error">
              Failed to load user.
            </p>
          )}
          {detailQuery.data && initialValues !== undefined && (
            <UserForm
              mode="edit"
              initialValues={initialValues}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              errorMessage={errorMessage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
